/**
 * Creative Schedule Storage
 * 
 * SINGLE SOURCE OF TRUTH for creative scheduled days in localStorage.
 * All schedule data flows through this module.
 * 
 * Flow:
 * 1. User selects days → setScheduledDays()
 * 2. At save time → getAllSchedules() returns clean data for DB
 * 3. After save → clearAllSchedules() to reset
 */

const STORAGE_KEY = "creative_schedules_v2";

export interface CreativeSchedule {
  creativeUrl: string;
  locationName: string;
  scheduledDays: number[];
  updatedAt: string;
}

interface ScheduleStore {
  schedules: CreativeSchedule[];
  version: number;
}

// ============ Internal Helpers ============

const getStore = (): ScheduleStore => {
  if (typeof window === "undefined") return { schedules: [], version: 2 };
  
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { schedules: [], version: 2 };
    
    const parsed = JSON.parse(raw);
    if (parsed?.version === 2 && Array.isArray(parsed.schedules)) {
      return parsed;
    }
    return { schedules: [], version: 2 };
  } catch {
    return { schedules: [], version: 2 };
  }
};

const saveStore = (store: ScheduleStore): void => {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
};

/**
 * Create a stable key for matching creatives
 */
const createKey = (url: string): string => {
  if (!url) return "";
  try {
    // Extract filename and remove query params
    const decoded = decodeURIComponent(url);
    const path = decoded.split("?")[0].split("#")[0];
    const filename = path.split("/").pop() || "";
    return filename.toLowerCase().trim();
  } catch {
    const path = url.split("?")[0].split("#")[0];
    return (path.split("/").pop() || "").toLowerCase().trim();
  }
};

// ============ Public API ============

/**
 * Set scheduled days for a creative
 */
export const setScheduledDays = (
  creativeUrl: string,
  locationName: string,
  days: number[]
): void => {
  if (!creativeUrl) return;
  
  const store = getStore();
  const key = createKey(creativeUrl);
  
  // Clean and validate days
  const cleanDays = days
    .map(d => Number(d))
    .filter(d => Number.isFinite(d) && d > 0)
    .sort((a, b) => a - b);
  
  // Remove duplicates
  const uniqueDays = [...new Set(cleanDays)];
  
  // Find existing or create new
  const existingIdx = store.schedules.findIndex(
    s => createKey(s.creativeUrl) === key && s.locationName === locationName
  );
  
  const schedule: CreativeSchedule = {
    creativeUrl,
    locationName,
    scheduledDays: uniqueDays,
    updatedAt: new Date().toISOString(),
  };
  
  if (existingIdx >= 0) {
    store.schedules[existingIdx] = schedule;
  } else {
    store.schedules.push(schedule);
  }
  
  saveStore(store);
  console.log(`[ScheduleStorage] Set days for ${key}@${locationName}: [${uniqueDays.join(",")}]`);
};

/**
 * Get scheduled days for a creative at a location
 */
export const getScheduledDays = (
  creativeUrl: string,
  locationName?: string
): number[] => {
  if (!creativeUrl) return [];
  
  const store = getStore();
  const key = createKey(creativeUrl);
  
  // First try exact location match
  if (locationName) {
    const exact = store.schedules.find(
      s => createKey(s.creativeUrl) === key && s.locationName === locationName
    );
    if (exact?.scheduledDays?.length) return exact.scheduledDays;
  }
  
  // Fallback to any matching URL
  const anyMatch = store.schedules.find(s => createKey(s.creativeUrl) === key);
  return anyMatch?.scheduledDays || [];
};

/**
 * Remove scheduled days for a creative
 */
export const removeScheduledDays = (creativeUrl: string, locationName?: string): void => {
  if (!creativeUrl) return;
  
  const store = getStore();
  const key = createKey(creativeUrl);
  
  if (locationName) {
    store.schedules = store.schedules.filter(
      s => !(createKey(s.creativeUrl) === key && s.locationName === locationName)
    );
  } else {
    store.schedules = store.schedules.filter(s => createKey(s.creativeUrl) !== key);
  }
  
  saveStore(store);
};

/**
 * Get all schedules (for saving to database)
 */
export const getAllSchedules = (): CreativeSchedule[] => {
  return getStore().schedules.filter(s => s.scheduledDays.length > 0);
};

/**
 * Clear all schedules (after successful save)
 */
export const clearAllSchedules = (): void => {
  saveStore({ schedules: [], version: 2 });
  console.log("[ScheduleStorage] Cleared all schedules");
};

/**
 * Build schedule lookup map for quick access
 */
export const buildScheduleLookup = (): Map<string, CreativeSchedule> => {
  const map = new Map<string, CreativeSchedule>();
  
  for (const schedule of getAllSchedules()) {
    const key = `${createKey(schedule.creativeUrl)}|${schedule.locationName}`;
    map.set(key, schedule);
    // Also set by URL key alone for fallback
    map.set(createKey(schedule.creativeUrl), schedule);
  }
  
  return map;
};

// ============ Legacy Compatibility ============
// These bridge to the old API while we migrate

/** @deprecated Use setScheduledDays instead */
export const setScheduledDaysForUrl = (url: string, days: number[]): void => {
  setScheduledDays(url, "", days);
};

/** @deprecated Use getScheduledDays instead */
export const getScheduledDaysForUrl = (url: string): number[] | undefined => {
  const days = getScheduledDays(url);
  return days.length > 0 ? days : undefined;
};

/** @deprecated Use getAllSchedules instead */
export const getScheduledDaysByUrlRecord = (): Record<string, number[]> => {
  const record: Record<string, number[]> = {};
  for (const schedule of getAllSchedules()) {
    record[schedule.creativeUrl] = schedule.scheduledDays;
  }
  return record;
};

// Kept for backward compatibility but no-op
export const normalizeCreativeUrl = (url: string): string => createKey(url);
export const removeScheduledDaysForUrl = (url: string): void => removeScheduledDays(url);
export const applyScheduledDaysToCreatives = <T extends { url?: string; scheduledDays?: number[] }>(
  creatives: T[]
): T[] => {
  return creatives.map(c => {
    if (!c?.url) return c;
    const stored = getScheduledDays(c.url);
    if (stored.length > 0 && (!c.scheduledDays || c.scheduledDays.length === 0)) {
      return { ...c, scheduledDays: stored };
    }
    return c;
  });
};
